import { inject } from '@angular/core';
import { CanMatchFn, Route, Router } from '@angular/router';
import { authAdminGuard, noAuthGuard } from '@ribera-web/auth-data-acess';
import { ShellAdmin, UnauthorizedAdmin } from '@ribera-web/shell-admin';
import { environment } from '../environments/environment';

const requireFeature = (
  feature: keyof NonNullable<typeof environment.features>
): CanMatchFn => {
  return () => {
    if (environment.features?.[feature] ?? true) {
      return true;
    }

    return inject(Router).parseUrl('/unauthorized');
  };
};

export const appRoutes: Route[] = [
  {
    path: 'login',
    canActivate: [noAuthGuard],
    loadChildren: () =>
      import('@ribera-web/feature-login-admin').then(
        (m) => m.featureLoginAdminRoutes
      ),
  },
  {
    path: '',
    component: ShellAdmin,
    canActivate: [authAdminGuard],
    canActivateChild: [authAdminGuard],
    children: [
      {
        path: 'dashboard',
        data: { breadcrumb: 'Dashboard' },
        children: [
          {
            path: 'gerencial',
            data: { breadcrumb: 'Gerencial' },
            loadComponent: () =>
              import('@ribera-web/admin-dashboard-feature').then(
                (m) => m.ManagerialDashboardFeature
              ),
          },
          {
            path: 'reservas',
            data: { breadcrumb: 'Reservas' },
            loadChildren: () =>
              import('@ribera-web/admin-dashboard-feature').then(
                (m) => m.adminDashboardFeatureRoutes
              ),
          },
          {
            path: 'fullday',
            data: { breadcrumb: 'Full Day' },
            loadComponent: () =>
              import('@ribera-web/fullday-manager-feature').then(
                (m) => m.FulldayDashboardFeature
              ),
          },
          {
            path: 'encuestas',
            data: { breadcrumb: 'Encuestas' },
            loadComponent: () =>
              import('@ribera-web/admin-dashboard-feature').then(
                (m) => m.SurveyDashboardFeature
              ),
          },
          {
            path: 'promoter',
            data: { breadcrumb: 'Promotores' },
            loadComponent: () =>
              import('@ribera-web/admin-dashboard-feature').then(
                (m) => m.PromoterDashboardFeature
              ),
          },
        ],
      },
      {
        path: 'gestor-de-ocupacion',
        loadChildren: () =>
          import('@ribera-web/occupancy-management-feature').then(
            (m) => m.occupancyManagementFeatureRoutes
          ),
      },
      {
        path: 'productos',
        data: { breadcrumb: 'Productos' },
        loadChildren: () =>
          import('@ribera-web/purchases-feature').then(
            (m) => m.purchasesFeatureRoutes
          ),
      },
      {
        path: 'gestor-de-pagos-manuales',
        loadChildren: () =>
          import('@ribera-web/payment-management-feature').then(
            (m) => m.paymentManagementRoutes
          ),
      },
      {
        path: 'gestor-de-pagos-parciales',
        canMatch: [requireFeature('partialPayments')],
        loadChildren: () =>
          import('@ribera-web/partial-payment-feature').then(
            (m) => m.partialPaymentFeatureRoutes
          ),
      },
      {
        path: 'gestor-de-pagos',
        canMatch: [requireFeature('newPaymentManager')],
        loadChildren: () =>
          import('@ribera-web/payment-manager').then(
            (m) => m.paymentManagerRoutes
          ),
      },
      {
        path: 'reservas/promociones',
        data: { breadcrumb: 'Promociones' },
        loadChildren: () =>
          import('@ribera-web/admin-promotions-feature').then((m) => [
            {
              path: '',
              component: m.AdminAvailablePromotions,
            },
            {
              path: ':promotionId/alojamientos',
              component: m.AdminOfferList,
            },
            {
              path: ':promotionId',
              component: m.AdminDetailPromotionsFeature,
            },
          ]),
      },
      {
        path: 'reservas',
        loadChildren: () =>
          import('@ribera-web/booking-management-feature').then(
            (m) => m.bookingManagementFeatureRoutes
          ),
      },
      {
        path: 'gestor-de-usuarios',
        loadChildren: () =>
          import('@ribera-web/users-management-feature').then(
            (m) => m.usersManagementFeatureRoutes
          ),
      },
      {
        path: 'paquetes-fulldays/tarifas',
        loadChildren: () =>
          import('@ribera-web/fullday-rate-feature').then(
            (m) => m.fulldayRateFeatureRoutes
          ),
      },
      {
        path: 'paquetes-fulldays/control-de-accesos-fulldays',
        loadChildren: () =>
          import('@ribera-web/fullday-access-feature').then(
            (m) => m.fulldayAccessFeatureRoutes
          ),
      },
      {
        path: 'paquetes-fulldays/control-de-accesos-fulldays/titular-reservas',
        loadChildren: () =>
          import('@ribera-web/booking-form-rate-access-ui').then(
            (m) => m.BookingFormRateAccessUi
          ),
      },
      {
        path: 'paquetes-fulldays/control-de-accesos-fulldays/pago-reservas',
        loadComponent: () =>
          import('@ribera-web/payment-feature').then((m) => m.PaymentFeature),
      },
      {
        path: 'paquetes-fulldays/reporte-de-registros',
        loadChildren: () =>
          import('@ribera-web/fullday-report-entry-feature').then(
            (m) => m.fulldayReportEntryFeatureRoutes
          ),
      },
      {
        path: 'gestor-de-contenidos',
        loadChildren: () =>
          import('@ribera-web/admin-cms-feature').then(
            (m) => m.adminCmsFeatureRoutes
          ),
      },
      {
        path: 'gestor-clientes',
        loadChildren: () =>
          import('@ribera-web/user-manager-feature').then(
            (m) => m.userManagerFeatureRoutes
          ),
      },
      {
        path: 'gestor-socios',
        loadChildren: () =>
          import('@ribera-web/beneficiary-feature').then(
            (m) => m.beneficiaryFeatureRoutes
          ),
      },
      {
        path: 'gestor-almacenes',
        loadChildren: () =>
          import('@ribera-web/warehouse-feature').then(
            (m) => m.warehouseFeatureRoutes
          ),
      },
      {
        path: 'gestor-combos',
        loadChildren: () =>
          import('@ribera-web/combo-feature').then((m) => m.comboFeatureRoutes),
      },
      {
        path: 'vencimientos',
        loadChildren: () =>
          import('@ribera-web/expiration-feature').then((m) => m.expirationFeatureRoutes),
      },
      {
        path: 'gestor-categorias',
        loadChildren: () =>
          import('@ribera-web/category-feature').then(
            (m) => m.categoryFeatureRoutes
          ),
      },
      {
        path: 'disponibilidad',
        loadChildren: () =>
          import('@ribera-web/availability-feature').then(
            (m) => m.availabilityFeatureRoutes
          ),
      },
      {
        path: 'productos-servicios-eventos',
        data: { breadcrumb: 'Productos/Servicios Eventos' },
        loadChildren: () =>
          import('@ribera-web/event-products-feature').then(
            (m) => m.eventProductsFeatureRoutes
          ),
      },
      {
        path: 'tipos-de-eventos',
        data: { breadcrumb: 'Tipos de Eventos' },
        loadChildren: () =>
          import('@ribera-web/event-types-feature').then(
            (m) => m.eventTypesFeatureRoutes
          ),
      },
      {
        path: 'solicitudes-de-cotizacion',
        data: { breadcrumb: 'Solicitudes de cotización' },
        loadChildren: () =>
          import('@ribera-web/event-quotes-feature').then(
            (m) => m.eventQuotesFeatureRoutes
          ),
      },
      {
        path: 'calendario-de-eventos',
        data: { breadcrumb: 'Calendario de eventos' },
        loadChildren: () =>
          import('@ribera-web/event-calendar-feature').then(
            (m) => m.eventCalendarFeatureRoutes
          ),
      },
      {
        path: 'gestor-promotores',
        loadChildren: () =>
          import('@ribera-web/promoters-manager-feature').then(
            (m) => m.promotersManagerFeatureRoutes
          ),
      },
      {
        path: 'gestor-promociones',
        loadChildren: () =>
          import('@ribera-web/admin-promotions-feature').then(
            (m) => m.adminPromotionsFeatureRoutes
          ),
      },
      {
        path: 'apartamentos',
        loadChildren: () =>
          import('@ribera-web/accommodation-management-feature').then(
            (m) => m.AccommodationManagementFeatureRoutes
          ),
      },
      {
        path: 'validacion-comisiones',
        loadChildren: () =>
          import('@ribera-web/commision-feature').then(
            (m) => m.validatorCommissionsRoutes
          ),
      },
      {
        path: 'retiros',
        loadChildren: () =>
          import('@ribera-web/commision-feature').then(
            (m) => m.withdrawalRequestRoutes
          ),
      },
      {
        path: 'auditoria',
        loadChildren: () =>
          import('@ribera-web/audit-feature').then((m) => m.auditRoutes),
      },
      {
        path: 'unauthorized',
        component: UnauthorizedAdmin,
      },
    ],
  },
  {
    path: '**',
    redirectTo: 'login',
  },
];
